import { useState } from 'react';
import { useNotification, useTranslate } from '@refinedev/core';
import { Button, Checkbox, Flex, Popover, Radio, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { axiosInstance } from '@/api/client';
import {
  RoyaltyExportGranularity,
  type ExportRoyaltyReportApiSettlementRoyaltyReportsIdExportGetParams,
  type RoyaltyReportOut,
} from '@/api/generated/schemas';
import {
  blobErrorMessage,
  filenameFromDisposition,
  saveBlob,
} from '@/utils/download';

const { Text } = Typography;

const T = 'settlement.royalty_reports';

/** The file is built synchronously; a daily sheet of a big group outlasts 30 s. */
const ROYALTY_EXPORT_TIMEOUT_MS = 120_000;

const GRANULARITIES = [
  RoyaltyExportGranularity.monthly,
  RoyaltyExportGranularity.daily,
];

interface RoyaltyReportExportButtonProps {
  record: Pick<RoyaltyReportOut, 'id' | 'period'>;
}

/**
 * The report as the xlsx the publisher portal hands out — the whole group, in
 * any status. Straight through axios rather than Refine or the generated
 * client: both return the parsed body only, with no headers and no Blob.
 * Every download is audited server-side, so it runs on a click and nothing
 * else — no prefetch, no retry.
 */
export const RoyaltyReportExportButton = ({
  record,
}: RoyaltyReportExportButtonProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const [open, setOpen] = useState(false);
  const [granularity, setGranularity] = useState<RoyaltyExportGranularity>(
    RoyaltyExportGranularity.monthly
  );
  const [splitImprints, setSplitImprints] = useState(false);
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    try {
      const params: ExportRoyaltyReportApiSettlementRoyaltyReportsIdExportGetParams =
        { granularity, split_imprints: splitImprints };
      const response = await axiosInstance.get<Blob>(
        `settlement/royalty_reports/${record.id}/export`,
        {
          params,
          responseType: 'blob',
          timeout: ROYALTY_EXPORT_TIMEOUT_MS,
        }
      );
      const disposition = response.headers['content-disposition'];
      // The header is invisible when CORS does not expose it.
      const filename =
        filenameFromDisposition(
          typeof disposition === 'string' ? disposition : undefined
        ) ??
        `rozliczenie-${dayjs(record.period).format('YYYY-MM')}-${granularity}.xlsx`;
      saveBlob(response.data, filename);
      setOpen(false);
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description:
          (await blobErrorMessage(error)) ?? t(`${T}.export.error_fallback`),
      });
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <Flex vertical gap={12}>
      <Flex vertical gap={4}>
        <Text strong>{t(`${T}.export.granularity_label`)}</Text>
        <Radio.Group
          value={granularity}
          onChange={(e) => setGranularity(e.target.value)}
          disabled={loading}
        >
          <Flex vertical gap={4}>
            {GRANULARITIES.map((value) => (
              <Radio key={value} value={value}>
                {t(`${T}.export.granularity.${value}`)}
              </Radio>
            ))}
          </Flex>
        </Radio.Group>
      </Flex>
      <Checkbox
        checked={splitImprints}
        onChange={(e) => setSplitImprints(e.target.checked)}
        disabled={loading}
      >
        {t(`${T}.export.split_imprints`)}
      </Checkbox>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading}
        disabled={loading}
        onClick={download}
      >
        {t(`${T}.export.submit`)}
      </Button>
    </Flex>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      // Stays open while the file is being built, so the spinner stays in sight.
      onOpenChange={(next) => {
        if (!loading) setOpen(next);
      }}
      content={content}
    >
      <Button icon={<DownloadOutlined />}>{t(`${T}.export.button`)}</Button>
    </Popover>
  );
};
